import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, Loader2, CheckCircle, XCircle, Clock, Filter,
  TrendingUp, DollarSign, ChevronRight
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';

interface Approval {
  id: string;
  dealId: string;
  soId: string;
  soNumber: string;
  type: 'PURCHASE' | 'LOGISTICS';
  sellPrice: number;
  commodityCost: number;
  logisticsCost: number;
  dealMargin: number;
  marginPercent: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  remarks: string | null;
  approvedBy: { firstName: string; lastName: string } | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  createdAt: string;
  deal: {
    commodity: string;
    buyerName: string;
    sellPrice: number;
    salesOrderId: string;
  } | null;
}

const STATUS_CONFIG = {
  PENDING: { label: 'Pending', icon: Clock, bg: 'bg-yellow-100', text: 'text-yellow-700' },
  APPROVED: { label: 'Approved', icon: CheckCircle, bg: 'bg-green-100', text: 'text-green-700' },
  REJECTED: { label: 'Rejected', icon: XCircle, bg: 'bg-red-100', text: 'text-red-700' },
};

export function FinancialApprovalsListPage() {
  const navigate = useNavigate();
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchApprovals();
  }, []);

  const fetchApprovals = async () => {
    try {
      const res = await api.get('/financial-approvals');
      setApprovals(res.data.data || []);
    } catch (err) {
      console.error('Failed to load approvals', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (soId: string, type: string, approved: boolean) => {
    try {
      await api.post(`/financial-approvals/${soId}`, { type, approved });
      fetchApprovals();
    } catch (err) {
      alert('Failed to submit approval');
    }
  };

  const filtered = approvals.filter(a => {
    if (filterStatus !== 'all' && a.status !== filterStatus) return false;
    if (filterType !== 'all' && a.type !== filterType) return false;
    return true;
  });

  const pendingCount = approvals.filter(a => a.status === 'PENDING').length;
  const approvedCount = approvals.filter(a => a.status === 'APPROVED').length;
  const rejectedCount = approvals.filter(a => a.status === 'REJECTED').length;
  const avgMargin = approvals.length > 0
    ? approvals.reduce((sum, a) => sum + (a.marginPercent || 0), 0) / approvals.length
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Financial Approvals</h1>
        <p className="text-sm text-gray-500">Review and approve deal margins before payment release</p>
      </div>

      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500"><Clock size={16} /> Pending</div>
          <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500"><CheckCircle size={16} /> Approved</div>
          <p className="text-2xl font-bold text-green-600">{approvedCount}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500"><XCircle size={16} /> Rejected</div>
          <p className="text-2xl font-bold text-red-600">{rejectedCount}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500"><TrendingUp size={16} /> Avg Margin</div>
          <p className="text-2xl font-bold text-gray-900">{avgMargin.toFixed(1)}%</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex items-center gap-3">
        <Filter size={16} className="text-gray-400" />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
          <option value="all">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
          <option value="all">All Types</option>
          <option value="PURCHASE">Purchase</option>
          <option value="LOGISTICS">Logistics</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left">
              <th className="px-4 py-3 font-medium text-gray-500">Deal / SO</th>
              <th className="px-4 py-3 font-medium text-gray-500">Type</th>
              <th className="px-4 py-3 font-medium text-gray-500">Sell Price</th>
              <th className="px-4 py-3 font-medium text-gray-500">Cost</th>
              <th className="px-4 py-3 font-medium text-gray-500">Margin</th>
              <th className="px-4 py-3 font-medium text-gray-500">Status</th>
              <th className="px-4 py-3 font-medium text-gray-500">Date</th>
              <th className="px-4 py-3 font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                  <ShieldCheck size={32} className="mx-auto mb-2 text-gray-300" />
                  No financial approvals found
                </td>
              </tr>
            ) : (
              filtered.map(a => {
                const statusCfg = STATUS_CONFIG[a.status] || STATUS_CONFIG.PENDING;
                const StatusIcon = statusCfg.icon;
                const totalCost = (a.type === 'PURCHASE' ? a.commodityCost : a.logisticsCost) || 0;
                const marginColor = a.marginPercent >= 10 ? 'text-green-600' : a.marginPercent >= 5 ? 'text-yellow-600' : 'text-red-600';

                return (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{a.soNumber}</div>
                      <div className="text-xs text-gray-500">{a.deal?.commodity || '—'} • {a.deal?.buyerName || '—'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        a.type === 'PURCHASE' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {a.type === 'PURCHASE' ? 'Purchase' : 'Logistics'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono">{formatINR(a.sellPrice)}</td>
                    <td className="px-4 py-3 font-mono">{formatINR(totalCost)}</td>
                    <td className="px-4 py-3">
                      <span className={`font-semibold ${marginColor}`}>{a.marginPercent?.toFixed(1)}%</span>
                      <span className="block text-xs text-gray-400">{formatINR(a.dealMargin)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${statusCfg.bg} ${statusCfg.text}`}>
                        <StatusIcon size={12} /> {statusCfg.label}
                      </span>
                      {a.approvedBy && (
                        <div className="text-xs text-gray-400 mt-0.5">by {a.approvedBy.firstName}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{formatDate(a.createdAt)}</td>
                    <td className="px-4 py-3">
                      {a.status === 'PENDING' ? (
                        <div className="flex gap-2">
                          <button onClick={() => handleApproval(a.soId, a.type, true)}
                            className="rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-200">
                            Approve
                          </button>
                          <button onClick={() => handleApproval(a.soId, a.type, false)}
                            className="rounded bg-red-100 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-200">
                            Reject
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => navigate(`/sales-orders/${a.soId}/financial-approval`)}
                          className="text-xs text-[var(--ink-0)] hover:underline flex items-center gap-1">
                          Details <ChevronRight size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
